import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { AuthStatusResponse, BrokerAccountsResponse } from '../types/api';
import { RefreshCw, Check, AlertTriangle, ExternalLink, Shield, Key, RefreshCcw, Lock, Building2 } from 'lucide-react';
import { formatDateTimeEastern } from '../lib/dateUtils';

export function Auth() {
  const [authStatus, setAuthStatus] = useState<AuthStatusResponse | null>(null);
  const [accounts, setAccounts] = useState<BrokerAccountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [selectingAccountId, setSelectingAccountId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = async () => {
    try {
      const status = await api.getAuthStatus();
      setAuthStatus(status);
      
      // Load accounts if authenticated
      if (status.is_authenticated) {
        try {
          const accountsData = await api.getBrokerAccounts();
          setAccounts(accountsData);
        } catch (error) {
          console.error('Error loading accounts:', error);
        }
      }
    } catch (error) {
      console.error('Error loading auth status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { login_url } = await api.getLoginUrl();
      window.open(login_url, '_blank', 'width=600,height=700');
    } catch (error) {
      console.error('Error getting login URL:', error);
    } finally {
      setConnecting(false);
    }
  };

  const handleSelectAccount = async (accountId: string) => {
    setSelectingAccountId(accountId);
    setError(null);
    try {
      await api.selectBrokerAccount(accountId);
      await loadStatus(); // Reload to get updated selection
    } catch (error) {
      console.error('Error selecting account:', error);
      setError('Failed to select account. Please try again.');
    } finally {
      setSelectingAccountId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading auth status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Broker Authentication</h1>
        <p className="text-gray-600 mt-1 text-base">Connect and manage your broker connection</p>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden ">
        <div className="p-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className={`w-24 h-24 rounded-lg flex items-center justify-center shadow-xl ${
              authStatus?.is_authenticated 
                ? 'bg-emerald-600 ' 
                : 'bg-amber-500 '
            }`}>
              {authStatus?.is_authenticated ? (
                <Check className="w-12 h-12 text-white" />
              ) : (
                <AlertTriangle className="w-12 h-12 text-white" />
              )}
            </div>
            
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">
                {authStatus?.broker || 'Broker'} Connection
              </h2>
              <p className={`mt-2 font-semibold text-lg ${
                authStatus?.is_authenticated ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {authStatus?.message || 'Unknown status'}
              </p>
              {authStatus?.expires_at && (
                <p className="mt-2 text-base text-gray-600 flex items-center gap-2">
                  <RefreshCcw className="w-5 h-5" />
                  Access token expires: {formatDateTimeEastern(authStatus.expires_at)}
                </p>
              )}
              {authStatus?.refresh_token_expires_at && (
                <p className={`mt-1 text-base flex items-center gap-2 ${
                  authStatus?.needs_reauth ? 'text-amber-600 font-medium' : 'text-gray-600'
                }`}>
                  <RefreshCcw className="w-5 h-5" />
                  {authStatus?.needs_reauth 
                    ? `Refresh token expired: ${formatDateTimeEastern(authStatus.refresh_token_expires_at)}`
                    : `Refresh token expires: ${formatDateTimeEastern(authStatus.refresh_token_expires_at)}`
                  }
                </p>
              )}
            </div>

            <button
              onClick={handleConnect}
              disabled={connecting}
              className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold rounded-xl transition-all  ${
                authStatus?.is_authenticated
                  ? 'bg-white text-gray-700 border-2 border-slate-200 shadow-md hover:border-emerald-400'
                  : 'bg-emerald-700 text-white shadow-md hover:bg-emerald-800'
              }`}
            >
              {connecting ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <ExternalLink className="w-5 h-5" />
              )}
              {authStatus?.is_authenticated ? 'Reconnect' : 'Connect Broker'}
            </button>
          </div>
        </div>
      </div>

      {authStatus?.is_authenticated && accounts && accounts.accounts.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden ">
          <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-700 flex items-center justify-center shadow-md">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Select Account</h2>
          </div>
          <div className="p-6 space-y-3">
            <p className="text-base text-gray-700 mb-4 font-medium">
              Choose which account to use for rebalancing orders:
            </p>
            {error && (
              <div className="flex items-center gap-3 px-5 py-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-base shadow-md">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span className="font-semibold">{error}</span>
              </div>
            )}
            {accounts.accounts.map((account) => (
              <button
                key={account.account_id}
                onClick={() => handleSelectAccount(account.account_id)}
                disabled={selectingAccountId !== null}
                className={`w-full text-left p-5 rounded-xl border-2 transition-all  ${
                  accounts.selected_account_id === account.account_id
                    ? 'border-emerald-600 bg-emerald-50 shadow-md'
                    : 'border-slate-200 bg-white hover:border-emerald-400 hover:shadow-md'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900 text-base">
                        {account.account_name || account.account_id}
                      </span>
                      {accounts.selected_account_id === account.account_id && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold bg-emerald-700 text-white shadow-md">
                          <Check className="w-3.5 h-3.5" />
                          Selected
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-base text-gray-600 font-medium">
                      <span>ID: {account.account_id}</span>
                      <span>•</span>
                      <span>{account.account_type}</span>
                    </div>
                  </div>
                  {selectingAccountId === account.account_id && (
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Shield className="w-4 h-4 text-emerald-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Security Information</h2>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">OAuth 2.0 Authentication</h3>
              <p className="text-sm text-gray-600 mt-1">
                Your credentials are never stored. We use industry-standard OAuth for secure authentication with your broker.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <RefreshCcw className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Automatic Token Refresh</h3>
              <p className="text-sm text-gray-600 mt-1">
                Access tokens are automatically refreshed when needed, keeping you connected without manual intervention.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Manual Execution Required</h3>
              <p className="text-sm text-gray-600 mt-1">
                By default, orders require manual confirmation before execution. No trades happen automatically unless explicitly enabled.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm bg-blue-50 border border-blue-200 text-blue-800">
        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
          <RefreshCcw className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <p className="font-medium">Note</p>
          <p className="text-sm opacity-80">
            After connecting, you may need to refresh the page to see the updated status. 
            The broker may require periodic re-authentication when refresh tokens expire.
          </p>
        </div>
      </div>
    </div>
  );
}
